import { useState, useEffect } from 'react';
import { validatePassword, passwordsMatch } from '../utils/validation';
import styles from './ValidatedInput.module.css';

/**
 * Password input field with real-time strength validation
 * - Shows password strength indicator
 * - Real-time validation feedback
 * - Requirements display
 */
export function PasswordInput({
    value,
    onChange,
    error,
    onValidationChange,
    confirmPassword = null,
    placeholder = 'Kodeord',
    disabled = false,
    showValidationFeedback = true
}) {
    const [touched, setTouched] = useState(false);
    const [validationResult, setValidationResult] = useState(null);
    const [showPassword, setShowPassword] = useState(false);

    // Validate on change
    useEffect(() => {
        if (value) {
            const result = validatePassword(value);
            setValidationResult(result);
            if (onValidationChange) {
                onValidationChange(result.valid);
            }
        } else {
            setValidationResult(null);
            if (onValidationChange) {
                onValidationChange(false);
            }
        }
    }, [value, onValidationChange]);

    const handleChange = (e) => {
        const newValue = e.target.value;
        // Enforce max length
        const truncated = newValue.slice(0, 128);
        onChange(truncated);
    };

    const handleBlur = () => {
        setTouched(true);
    };

    const showError = touched && validationResult && !validationResult.valid;
    const showSuccess = touched && validationResult && validationResult.valid;

    let strengthColor = '#d97706';
    let strengthLabel = 'Svag';
    if (validationResult?.strength === 'medium') {
        strengthColor = '#f59e0b';
        strengthLabel = 'Moderat';
    } else if (validationResult?.strength === 'strong') {
        strengthColor = '#10b981';
        strengthLabel = 'Stærk';
    }

    return (
        <div className={styles.fieldWrapper}>
            <div className={styles.passwordInputWrapper}>
                <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder={placeholder}
                    value={value}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={disabled}
                    className={`${styles.input} ${showError ? styles.inputError : ''} ${showSuccess ? styles.inputSuccess : ''}`}
                    maxLength={128}
                    autoComplete="new-password"
                />
                {value && (
                    <button
                        type="button"
                        className={styles.togglePasswordBtn}
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={disabled}
                        aria-label={showPassword ? 'Skjul kodeord' : 'Vis kodeord'}
                    >
                        {showPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                )}
            </div>

            {value && (
                <div className={styles.charCount}>
                    {value.length}/128
                </div>
            )}

            {value && validationResult && showValidationFeedback && (
                <div className={styles.strengthIndicator} style={{ borderColor: strengthColor }}>
                    <div className={styles.strengthBar}>
                        <div
                            className={styles.strengthFill}
                            style={{
                                width: validationResult.strength === 'weak' ? '33%' : validationResult.strength === 'medium' ? '66%' : '100%',
                                backgroundColor: strengthColor
                            }}
                        />
                    </div>
                    <span style={{ color: strengthColor }}>{strengthLabel} kodeord</span>
                </div>
            )}

            {showError && (
                <div className={styles.errorMessage}>
                    ⚠️ {validationResult.error}
                </div>
            )}

            {showSuccess && showValidationFeedback && (
                <div className={styles.successMessage}>
                    ✓ Kodeord accepteret ({validationResult.strength} styrke)
                </div>
            )}

            {!touched && value && !error && showValidationFeedback && (
                <div className={styles.hintMessage}>
                    💡 Kræver: 1 stort bogstav, 1 lille bogstav, 1 tal
                </div>
            )}

            {error && (
                <div className={styles.serverError}>
                    {error}
                </div>
            )}

            {confirmPassword !== null && value !== confirmPassword && value && (
                <div className={styles.warningMessage}>
                    ⚠️ Kodeordene stemmer ikke overens
                </div>
            )}
        </div>
    );
}
